import {PropertyDefinitionForm} from '@github-ui/custom-properties-core/components/PropertyDefinitionForm'
import {useBannerContext} from '@github-ui/custom-properties-core/context/BannerContext'
import {createDefinitionForm} from '@github-ui/custom-properties-core/definition-form'
import type {DefinitionFormSchema, DefinitionValidator} from '@github-ui/custom-properties-core/definition-form-types'
import {
  getFirstInvalidFieldKey,
  usePropertyDefinitionForm,
} from '@github-ui/custom-properties-core/hooks/use-property-definition-form'
import {useRefs} from '@github-ui/custom-properties-core/hooks/use-refs'
import {validatePropertyNameAsync} from '@github-ui/custom-properties-editing/validate'
import type {
  PropertiesLimits,
  PropertyDefinition,
  PropertyNameOrgConflicts,
  ValuesEditableBy,
} from '@github-ui/custom-properties-types'
import type {SafeHTMLString} from '@github-ui/safe-html'
import sudo from '@github-ui/sudo'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {Checkbox, FormControl} from '@primer/react'
import {useEffect, useMemo, useState} from 'react'

import {useSavePropertyPath} from '../hooks/use-properties-paths'
import {usePropertySource} from '../hooks/use-property-source'

interface RepoPropertyDefinitionSchema extends DefinitionFormSchema {
  valuesEditableBy: ValuesEditableBy
}

interface Props {
  definition?: PropertyDefinition
  // Make required once `repo_custom_properties_configurable_limits` FF is dropped
  propertiesLimits: PropertiesLimits | undefined
  existingPropertyNames: string[]
  setFormError: (error: SafeHTMLString) => void
  onCancel: () => void
  onSuccess: (propertyName: string) => void
}

export function DefinitionForm({
  definition,
  propertiesLimits,
  existingPropertyNames,
  setFormError,
  onCancel,
  onSuccess,
}: Props) {
  const {settingsLevel, sourceName} = usePropertySource()
  const {setBanner, bannerRef, banner} = useBannerContext()
  const [orgConflicts, setOrgConflicts] = useState<PropertyNameOrgConflicts>()
  const {getRef, setRef} = useRefs<RepoPropertyDefinitionSchema>()
  const [saving, setSaving] = useState(false)
  const savePropertyPath = useSavePropertyPath()

  const isEditing = Boolean(definition)

  const businessSlug = settingsLevel === 'business' ? sourceName : undefined
  const form = useMemo(
    () =>
      createDefinitionForm<RepoPropertyDefinitionSchema>([
        {
          field: 'propertyName',
          validate: validatePropertyName(businessSlug, setOrgConflicts),
        },
      ]),
    [businessSlug],
  )

  useEffect(() => {
    bannerRef?.current?.focus()
  }, [banner, bannerRef])

  const {updateField, getFieldValue, getFieldError, validateForm, validateField} =
    usePropertyDefinitionForm<RepoPropertyDefinitionSchema>({
      form,
      formMode: isEditing ? 'update' : 'create',
      propertiesLimits,
      existingPropertyNames,
      initialValues: {
        propertyName: definition?.propertyName || '',
        description: definition?.description || '',
        valueType: definition?.valueType || 'string',
        allowedValues: definition?.allowedValues || [],
        defaultValue: definition?.defaultValue || '',
        regexEnabled: !!definition?.regex,
        regexPattern: definition?.regex || '',
        required: definition?.required || false,
        newAllowedValue: '',
        valuesEditableBy: definition?.valuesEditableBy || 'org_actors',
      },
    })

  async function saveDefinition() {
    setFormError('' as SafeHTMLString)
    const errors = await validateForm()

    if (errors.formErrors.length) {
      return setFormError(errors.formErrors[0] as SafeHTMLString)
    }

    const key = getFirstInvalidFieldKey(errors)
    if (key) {
      return getRef(key)?.focus()
    }

    setSaving(true)

    if (!(await sudo())) {
      setFormError('Unauthorized' as SafeHTMLString)
      setSaving(false)
      return
    }

    const valueType = getFieldValue('valueType')
    const allowedValues =
      valueType === 'single_select' || valueType === 'multi_select' ? getFieldValue('allowedValues') : null
    const regex = (getFieldValue('regexEnabled') && getFieldValue('regexPattern')) || null

    const body = {
      propertyName: getFieldValue('propertyName'),
      valueType,
      required: getFieldValue('required'),
      defaultValue: getFieldValue('required') ? getFieldValue('defaultValue') : null,
      description: getFieldValue('description')?.trim() || null,
      allowedValues,
      valuesEditableBy: getFieldValue('valuesEditableBy'),
      regex,
    }

    try {
      const result = await verifiedFetchJSON(savePropertyPath, {
        method: 'POST',
        body,
      })

      if (result.ok) {
        setBanner(isEditing ? 'definition.updated.success' : 'definition.created.success')
        onSuccess(body.propertyName)
      } else {
        const responseBody = await result.json()
        setFormError((responseBody?.error || 'Something went wrong.') as SafeHTMLString)
        setSaving(false)
      }
    } catch {
      setFormError('Something went wrong.' as SafeHTMLString)
      setSaving(false)
    }
  }

  const valueType = getFieldValue('valueType')
  const isSelect = valueType === 'single_select' || valueType === 'multi_select'
  const isText = valueType === 'string'

  const setNewAllowedValueRef = useMemo(() => setRef('newAllowedValue'), [setRef])

  return (
    <>
      <PropertyDefinitionForm>
        <PropertyDefinitionForm.NameField
          inputRef={setRef('propertyName')}
          readonly={isEditing}
          value={getFieldValue('propertyName')}
          validationError={getFieldError('propertyName')}
          onChange={value => updateField('propertyName', value)}
          orgConflicts={orgConflicts}
        />

        <PropertyDefinitionForm.DescriptionField
          inputRef={setRef('description')}
          value={getFieldValue('description') || ''}
          validationError={getFieldError('description')}
          onChange={value => updateField('description', value)}
        />

        <PropertyDefinitionForm.TypeSelector
          value={valueType}
          readonly={isEditing}
          onChange={value => updateField('valueType', value)}
        />

        {isSelect && (
          <PropertyDefinitionForm.AllowedValuesEditor
            setNewAllowedValueRef={setNewAllowedValueRef}
            allowedValues={getFieldValue('allowedValues')}
            newAllowedValue={getFieldValue('newAllowedValue')}
            newAllowedValueError={getFieldError('newAllowedValue')}
            defaultValue={getFieldValue('defaultValue')}
            onNewAllowedValueChange={value => updateField('newAllowedValue', value)}
            onAllowedValuesChange={async ({type, values}) => {
              if (type === 'add') {
                if (await validateField('newAllowedValue')) {
                  return getRef('newAllowedValue')?.focus()
                }
                updateField('newAllowedValue', '', {skipValidation: true})
              }

              updateField('allowedValues', values)
            }}
          />
        )}

        <PropertyDefinitionForm.AdditionalSettingsSection>
          {isText && (
            <PropertyDefinitionForm.RegexEditor
              inputRef={setRef('regexPattern')}
              isEnabled={getFieldValue('regexEnabled')}
              pattern={getFieldValue('regexPattern') || ''}
              validationError={getFieldError('regexPattern')}
              onEnabledChange={enabled => updateField('regexEnabled', enabled)}
              onPatternChange={pattern => updateField('regexPattern', pattern, {debounce: true})}
            />
          )}

          <FormControl>
            <Checkbox
              checked={getFieldValue('valuesEditableBy') === 'org_and_repo_actors'}
              onChange={event =>
                updateField('valuesEditableBy', event.target.checked ? 'org_and_repo_actors' : 'org_actors')
              }
            />
            <FormControl.Label>Allow repository actors to set this property</FormControl.Label>
            <FormControl.Caption>
              Repository users and apps with the repository-level &quot;custom properties&quot; fine-grained permission
              can set and update the value for their repository.
            </FormControl.Caption>
          </FormControl>

          <PropertyDefinitionForm.RequiredDefinitionEditor
            checkboxLabel="Require this property for all repositories"
            checkboxCaption="Repositories that don't have an explicit value for this property will inherit the default value."
            defaultValueCaption="This is the default value that will be set for this property. Changing it has immediate effect, although on large organizations it may take minutes to be available in search results."
            setDefaultValueInputRef={setRef('defaultValue')}
            isRequired={getFieldValue('required')}
            valueType={valueType}
            defaultValue={(getFieldValue('defaultValue') || '') as string}
            defaultValueError={getFieldError('defaultValue')}
            allowableDefaultValues={getFieldValue('allowedValues')}
            propertyName={getFieldValue('propertyName')}
            onRequiredChange={required => updateField('required', required)}
            onDefaultValueChange={value => updateField('defaultValue', value, {debounce: true})}
          />
        </PropertyDefinitionForm.AdditionalSettingsSection>

        <PropertyDefinitionForm.Actions isSaving={saving} onSave={saveDefinition} onCancel={onCancel} />
      </PropertyDefinitionForm>
    </>
  )
}

function validatePropertyName(
  businessSlug: string | undefined,
  onOrgConflicts: (conflicts: PropertyNameOrgConflicts | undefined) => void,
): DefinitionValidator<RepoPropertyDefinitionSchema> {
  return async (data, errors, ctx) => {
    if (!businessSlug) return
    if (ctx.formMode === 'update') return

    onOrgConflicts(undefined)

    // Save server request if property name is invalid for other reasons
    if (errors.fieldErrors.propertyName?.length) {
      return
    }

    const {propertyName} = data

    const error = await validatePropertyNameAsync(businessSlug, propertyName)

    if (error) {
      if (error?.orgConflicts) {
        onOrgConflicts(error.orgConflicts)
      }
      return {fieldErrors: {propertyName: [error.message]}}
    }
  }
}

try{ DefinitionForm.displayName ||= 'DefinitionForm' } catch {}